/* eslint-disable jsx-a11y/anchor-is-valid */
import { Fragment, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import ApexCharts, { ApexOptions } from 'apexcharts'
import { useThemeMode } from '_metronic/partials'
import Datetime from 'react-datetime'
// import { extractSingleBarChart } from 'Utils/ApexChartsTooltipsExtractration'
import { useProjectStore } from 'Store/Project'
import formatNumberCommas from 'Utils/formatNumberCommas'
import moment from 'moment'
import 'moment-timezone'
import { shallow } from 'zustand/shallow'

const getWeeklyDefaultRange = () => ({
  from: moment().subtract(6, 'days').startOf('day').toISOString(),
  to: moment().endOf('day').toISOString(),
})

const getJobDateRange = (startJobDate: string, endJobDate: string) => ({
  from: startJobDate,
  to: endJobDate,
})

const Progress7DaysChart = () => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const { mode } = useThemeMode()

  const { id } = useParams<{ page: string; id: string }>()
  const weeklyDefault = getWeeklyDefaultRange()
  const [from, setFrom] = useState(weeklyDefault.from)
  const [to, setTo] = useState(weeklyDefault.to)
  const initializedProjectId = useRef<string | null>(null)
  const { weeklyReport, getWeeklyReport, projectData } = useProjectStore(
    state => ({
      weeklyReport: state.weeklyReport,
      getWeeklyReport: state.getWeeklyReport,
      projectData: state.selected,
    }),
    shallow
  )

  useEffect(() => {
    if (!id || !projectData?.id || String(projectData.id) !== String(id)) {
      return
    }
    if (initializedProjectId.current === id) {
      return
    }

    initializedProjectId.current = id

    const { startJobDate, endJobDate } = projectData
    if (
      startJobDate &&
      endJobDate &&
      moment(startJobDate).isValid() &&
      moment(endJobDate).isValid()
    ) {
      const jobRange = getJobDateRange(startJobDate, endJobDate)
      setFrom(jobRange.from)
      setTo(jobRange.to)
      return
    }

    const fallback = getWeeklyDefaultRange()
    setFrom(fallback.from)
    setTo(fallback.to)
  }, [id, projectData])

  const fetchData = async () => {
    if (id) {
      await getWeeklyReport(parseInt(id), from, to)
    }
  }

  const refreshChart = (
    rows: WeeklyReportRow[],
    planStatusColor?: string
  ) => {
    if (!chartRef.current) {
      return
    }

    const chart = new ApexCharts(chartRef.current, getChartOptions(rows, planStatusColor))
    if (chart) {
      chart.render()
    }

    return chart
  }

  useEffect(() => {
    const rows = weeklyReport?.data ?? []
    const chart = refreshChart(rows, weeklyReport?.planStatusColor)

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRef, mode, weeklyReport])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to])

  return (
    <Fragment>
      <div className='mb-5 d-flex flex-column flex-sm-row justify-content-sm-end align-items-sm-center gap-4'>
        <div>
          <div className='fs-6 mb-3'>วันที่เริ่มต้น</div>
          <Datetime
            locale='en'
            dateFormat='DD/MM/YYYY'
            timeFormat={false}
            renderInput={props => {
              return (
                <input
                  {...props}
                  autoComplete='off'
                  placeholder='เลือกวันที่'
                  value={moment(from).isValid() ? props.value : ''}
                />
              )
            }}
            closeOnClickOutside
            className='filter-date-input w-100 z-4'
            value={moment(from).format('DD/MM/YYYY')}
            onChange={(val: any) => setFrom(moment(val).startOf('day').toISOString())}
            isValidDate={(currentDate: any) => {
              return currentDate.isAfter(moment(to).subtract(1, 'day')) === false
            }}
          />
        </div>
        <div className='mt-5 mt-sm-0'>
          <div className='fs-6 mb-3'>วันที่สิ้นสุด</div>
          <Datetime
            locale='en'
            dateFormat='DD/MM/YYYY'
            timeFormat={false}
            renderInput={props => {
              return (
                <input
                  {...props}
                  autoComplete='off'
                  placeholder='เลือกวันที่'
                  value={moment(to).isValid() ? props.value : ''}
                />
              )
            }}
            closeOnClickOutside
            className='filter-date-input w-100 z-4'
            value={moment(to).format('DD/MM/YYYY')}
            onChange={(val: any) => setTo(moment(val).endOf('day').toISOString())}
            isValidDate={(currentDate: any) => {
              return currentDate.isBefore(moment(from)) === false
            }}
          />
        </div>
      </div>

      {/* begin::Chart */}
      <div
        ref={chartRef}
        id='kt_charts_member_types_chart'
        style={{ height: '300px' }}
        className='card-rounded-bottom'></div>
      {/* end::Chart */}
    </Fragment>
  )
}

type WeeklyReportRow = {
  date?: string
  totalCompleted?: number
  totalWasted?: number
  dailyCompleted?: number
  dailyTargetPiles?: number
  accSchedulePiles?: number | null
  accSchedulePercentage?: number
  accActualPercentage?: number
}

const COLOR_COMPLETED = '#50CD89'
const COLOR_WASTED = '#FFA800'
const COLOR_SCHEDULE = '#009EF7'

const PLAN_STATUS_COLORS: Record<string, string> = {
  green: '#50CD89',
  yellow: '#FFC700',
  red: '#F1416C',
}

const formatPercentage = (value: unknown): string | null => {
  const num = Number(value)
  if (Number.isNaN(num)) {
    return null
  }
  return `${num.toFixed(1)}%`
}

const mapPlanStatusColor = (planStatusColor: unknown): string => {
  if (typeof planStatusColor !== 'string') {
    return COLOR_COMPLETED
  }
  return PLAN_STATUS_COLORS[planStatusColor] ?? COLOR_COMPLETED
}

function getSchedulePointValue (accSchedulePiles: unknown): number | null {
  if (accSchedulePiles == null) {
    return null
  }

  const value = Number(accSchedulePiles)
  if (Number.isNaN(value) || value <= 0) {
    return null
  }

  return value
}

function getChartOptions (rows: WeeklyReportRow[], planStatusColor?: string): ApexOptions {
  const actualColor = mapPlanStatusColor(planStatusColor)
  const scheduleData = rows.map(report => getSchedulePointValue(report.accSchedulePiles))
  const showSchedule =
    rows.some(row => row.accSchedulePiles != null) && scheduleData.some(value => value != null)

  const categories = rows.map(report => moment(report.date).format('DD MMM'))

  const series: Array<{ name: string; data: Array<number | null> }> = [
    {
      name: 'ตอกสำเร็จ',
      data: rows.map(report => report.totalCompleted ?? 0),
    },
    {
      name: 'ตอกเสีย',
      data: rows.map(report => report.totalWasted ?? 0),
    },
  ]

  if (showSchedule) {
    series.push({
      name: 'เป้าหมาย',
      data: scheduleData,
    })
  }

  return {
    grid: {
      padding: {
        top: 60,
      },
    },
    series,
    colors: showSchedule
      ? [actualColor, COLOR_WASTED, COLOR_SCHEDULE]
      : [actualColor, COLOR_WASTED],
    chart: {
      type: 'line',
      height: 300,
      toolbar: {
        show: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: 'smooth',
      width: 3,
    },
    tooltip: {
      shared: true,
      intersect: false,
      followCursor: true,
      custom: ({ dataPointIndex, w }) => {
        const row = rows[dataPointIndex]
        if (!row) {
          return ''
        }

        const date = w.globals.categoryLabels[dataPointIndex] ?? categories[dataPointIndex] ?? ''
        const dailyCompleted = Number(row.dailyCompleted ?? 0)
        const totalCompleted = Number(row.totalCompleted ?? 0)
        const dailyTarget = Number(row.dailyTargetPiles ?? 0)
        const accSchedule = Number(row.accSchedulePiles ?? 0)
        const totalWasted = Number(row.totalWasted ?? 0)
        const actualPct = formatPercentage(row.accActualPercentage) ?? '0.0%'
        const schedulePct = formatPercentage(row.accSchedulePercentage) ?? '0.0%'

        const swatch = (color: string) =>
          `<span style="background:${color};width:10px;height:10px;border-radius:50%;display:inline-block;flex-shrink:0"></span>`

        const line = (color: string, label: string, value: string) =>
          `<div class="d-flex align-items-center gap-2 py-1">${swatch(color)}<span>${label}: ${value}</span></div>`

        return `<div class="apexcharts-tooltip-custom px-3 py-2">
          ${date ? `<div class="fw-bold mb-1">${date}</div>` : ''}
          ${line(actualColor, 'ตอกสำเร็จต่อวัน', `${formatNumberCommas(dailyCompleted)} ต้น`)}
          ${line(actualColor, 'ตอกสำเร็จสะสม', `${formatNumberCommas(totalCompleted)} ต้น`)}
          ${line(actualColor, '% ตอกสำเร็จสะสม', actualPct)}
          <div class="border-top mt-2 pt-2">
            ${line(COLOR_SCHEDULE, 'เป้าหมายต่อวัน', `${formatNumberCommas(dailyTarget)} ต้น`)}
            ${line(COLOR_SCHEDULE, 'เป้าหมายสะสม', `${formatNumberCommas(accSchedule)} ต้น`)}
            ${line(COLOR_SCHEDULE, '% เป้าหมายสะสม', schedulePct)}
          </div>
          <div class="border-top mt-2 pt-2">
            ${line(COLOR_WASTED, 'ตอกเสีย', `${formatNumberCommas(totalWasted)} ต้น`)}
          </div>
        </div>`
      },
    },
    markers: {
      size: 4,
      strokeWidth: 2,
      colors: showSchedule
        ? [actualColor, COLOR_WASTED, COLOR_SCHEDULE]
        : [actualColor, COLOR_WASTED],
      hover: {
        size: 6,
      },
    },
    xaxis: {
      type: 'category',
      categories,
    },
    fill: {
      type: 'solid',
      opacity: 1,
    },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'right',
      floating: true,
      markers: {
        width: 12,
        height: 12,
        radius: 6,
      },
      formatter: function (seriesName) {
        return seriesName + '\n'
      },
      onItemHover: {
        highlightDataSeries: true,
      },
    },
  }
}

export default Progress7DaysChart
